import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CHANNELS = [["EMAIL", "Email"], ["PHONE", "Phone call"], ["PORTAL_MESSAGE", "Portal message"], ["IN_PERSON", "In person"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Target({ label, t }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
      <span className="w-28 text-jigsaw-muted">{label}</span>
      <StatusBadge status={t.state} />
      {t.due_at && <span className="text-jigsaw-muted">due {fmt(t.due_at)}</span>}
      {t.achieved_at && <span className="text-jigsaw-muted">· recorded {fmt(t.achieved_at)}</span>}
      {t.breached && (t.late_hours != null || t.overdue_hours != null) && <span className="font-medium text-jigsaw-danger">· {t.late_hours ?? t.overdue_hours}h past target</span>}
    </div>
  );
}

export function ResponseTargetPanel({ enquiry, onChanged }) {
  const sla = enquiry.sla;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ channel: "", summary: "" });
  const terminal = ["COMPLETED", "CANCELLED"].includes(enquiry.status);
  const canRecord = !terminal && !sla?.first_response?.achieved_at;
  const submit = async () => {
    try {
      const r = await api.post(`/enquiries/${enquiry.id}/first-response`, form);
      toast[r.data.already_recorded ? "info" : "success"](r.data.already_recorded ? "A first response was already on record" : "First response recorded (audited)");
      setOpen(false); setForm({ channel: "", summary: "" }); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Could not record first response"); }
  };
  return (
    <div data-testid="response-target-panel" className="rounded-[10px] border border-jigsaw-border bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] font-semibold text-jigsaw-navy">Response targets <span className="ml-1 font-normal text-jigsaw-muted">(elapsed UTC hours)</span></p>
        {canRecord && <Button data-testid="record-first-response-button" size="sm" variant="outline" onClick={() => setOpen(true)}>Record first response</Button>}
      </div>
      {!sla || !sla.governed ? (
        <p data-testid="response-target-unconfigured" className="mt-2 text-[13px] text-jigsaw-muted">Not configured for this enquiry - no targets were in force when it was raised. Targets are set by a Jigsaw Admin under Admin › Response targets and apply to enquiries raised afterwards.</p>
      ) : (
        <div className="mt-2 space-y-1.5">
          <Target label="First response" t={sla.first_response} />
          <Target label="Resolution" t={sla.resolution} />
          <p className="text-[11px] text-jigsaw-muted">Policy v{sla.policy_version} snapshot at creation - later target changes never alter this record. First response counts only when recorded here with evidence; assignment and drafts do not.</p>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="first-response-dialog">
          <DialogHeader><DialogTitle>Record first response to client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Channel</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="first-response-channel"><SelectValue placeholder="How did you respond?" /></SelectTrigger>
                <SelectContent>{CHANNELS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>What was said (evidence)</Label>
              <Textarea data-testid="first-response-summary" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="e.g. Emailed the booker to acknowledge the request and confirm sourcing has started." /></div>
            <p className="text-[12px] text-jigsaw-muted">Recorded at the current time against your account and written to the audit trail. The first record stands; it cannot be edited.</p>
          </div>
          <DialogFooter><Button data-testid="first-response-submit" onClick={submit} disabled={!form.channel || form.summary.trim().length < 5}>Record</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
